import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  useDeleteMenuItemOptionMutation,
  useGetMenuItemOptionQuery,
} from "@/redux/services/menu/menu.api";
import { MenuItemOption } from "@/redux/services/menu/menu.type";
import { displayPrice, formatStock } from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import {
  CheckSquare,
  List,
  Pencil,
  ToggleLeft,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";

interface Props {
  optionId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (option: MenuItemOption) => void;
}

const typeConfig: Record<
  string,
  {
    label: string;
    icon: React.FC<{ className?: string }>;
    color: string;
  }
> = {
  select: {
    label: "Liste déroulante",
    icon: List,
    color: "bg-blue-100 text-blue-700",
  },
  radio: {
    label: "Bouton radio",
    icon: ToggleLeft,
    color: "bg-purple-100 text-purple-700",
  },
  checkbox: {
    label: "Case à cocher",
    icon: CheckSquare,
    color: "bg-accent-light text-accent",
  },
};

const MetaRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-card-foreground font-medium">{value}</span>
  </div>
);

const MenuItemOptionDetailsDrawer = ({
  optionId,
  isOpen,
  onClose,
  onEdit,
}: Props) => {
  const currency = useRestaurantCurrency();
  const confirm = useConfirm();
  const { data: option, isFetching } = useGetMenuItemOptionQuery(
    optionId ?? "",
    { skip: !optionId }
  );
  const [deleteOption, { isLoading: isDeleting }] =
    useDeleteMenuItemOptionMutation();

  const type = option ? typeConfig[option.type] ?? typeConfig.select : null;
  const TypeIcon = type?.icon ?? List;

  const handleDelete = async () => {
    if (!option) return;
    const ok = await confirm({
      title: `Supprimer "${option.title}" ?`,
      description:
        "Cette option sera retirée de tous les articles qui l'utilisent.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    const res = await deleteOption(option.id);
    if ("error" in res) {
      toast.error("Erreur lors de la suppression");
      return;
    }
    toast.success("Option supprimée");
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0"
      >
        <header className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
          <SheetTitle className="text-base">
            {option ? option.title : "Détails de l'option"}
          </SheetTitle>
          <SheetDescription hidden>
            Détails et actions pour cette option de menu.
          </SheetDescription>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {isFetching && !option ? (
            <div className="space-y-3">
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : option ? (
            <>
              <section className="space-y-3">
                {type && (
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${type.color}`}
                  >
                    <TypeIcon className="h-3 w-3" />
                    {type.label}
                  </span>
                )}
                <div className="space-y-2">
                  <MetaRow label="Position" value={option.sortOrder} />
                  <MetaRow
                    label="Afficher le titre"
                    value={option.shouldDisplayTitle ? "Oui" : "Non"}
                  />
                  <MetaRow
                    label="Template"
                    value={option.isTemplate ? "Oui" : "Non"}
                  />
                  {option.dependsOnOption && (
                    <MetaRow
                      label="Dépend de"
                      value={option.dependsOnOption.title}
                    />
                  )}
                </div>
                {option.isTemplate && option.templateDescription && (
                  <p className="text-sm text-muted-foreground bg-muted/50 rounded-md p-3">
                    {option.templateDescription}
                  </p>
                )}
              </section>

              <section className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-border pb-2">
                  Valeurs ({option.menuItemOptionValues.length})
                </h3>
                {option.menuItemOptionValues.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Aucune valeur
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {option.menuItemOptionValues.map((value) => {
                      const ingredients =
                        value.menuItemOptionValueIngredients ?? [];
                      return (
                        <li
                          key={value.id}
                          className="border border-border rounded-md p-3 space-y-2"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-card-foreground text-sm">
                              {value.title}
                            </span>
                            {option.isTemplate &&
                              value.basePrice !== undefined && (
                                <span className="text-xs text-muted-foreground">
                                  {displayPrice(value.basePrice, currency)}
                                </span>
                              )}
                          </div>
                          {ingredients.length > 0 && (
                            <ul className="space-y-1 pt-1 border-t border-border">
                              {ingredients.map((link) => (
                                <li
                                  key={link.id}
                                  className="flex items-center justify-between text-xs"
                                >
                                  <span className="text-card-foreground">
                                    {link.ingredient.name
                                      ? link.ingredient.name
                                      : "—"}
                                  </span>
                                  <span className="text-muted-foreground">
                                    {formatStock(
                                      link.quantity,
                                      link.ingredient.baseUnit ?? ""
                                    )}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Impossible de charger les détails.
            </p>
          )}
        </div>

        <footer className="flex-shrink-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-2">
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!option || isDeleting}
          >
            <Trash2 className="h-4 w-4" />
            Supprimer
          </Button>
          <Button
            type="button"
            variant="accent"
            onClick={() => option && onEdit(option)}
            disabled={!option}
          >
            <Pencil className="h-4 w-4" />
            Modifier
          </Button>
        </footer>
      </SheetContent>
    </Sheet>
  );
};

export default MenuItemOptionDetailsDrawer;
